import { useEffect, useRef, useState } from "react";
import { FolderOpen, Layers, Loader2, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText, parseEpisode, PRESET_LABELS } from "@/lib/api";
import { fmtBytes } from "@/lib/format";

const MEDIA_RE = /\.(wav|flac|aif|aiff|mp4|mov|mxf|mkv|mp3|m4a)$/i;
const inputCls = "bg-[#0B0E14] border border-[#253044] rounded-sm px-2 py-1 text-xs font-mono focus:outline-none focus:border-cyan-500";

const readEntry = (entry, path = "") => new Promise((resolve) => {
  if (entry.isFile) return entry.file((f) => resolve([{ file: f, path: `${path}${f.name}` }]));
  const reader = entry.createReader(); const all = [];
  const next = () => reader.readEntries(async (entries) => {
    if (!entries.length) return resolve(all);
    for (const e of entries) all.push(...(await readEntry(e, `${path}${entry.name}/`)));
    next();
  });
  next();
});

const toRows = (files) => files.filter((x) => MEDIA_RE.test(x.file.name)).sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }))
  .map((x, i) => ({ id: `${x.path}-${i}`, file: x.file, path: x.path, project: x.path.includes("/") ? x.path.split("/")[0] : "", episode: parseEpisode(x.file.name, i), status: "pending", pct: 0 }));

export const BatchIntake = ({ onDone }) => {
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [engines, setEngines] = useState([]);
  const [engine, setEngine] = useState("");
  const [preset, setPreset] = useState("DME");
  const [running, setRunning] = useState(false);
  const [drag, setDrag] = useState(false);
  useEffect(() => { api.get("/engines").then((r) => { setEngines(r.data.engines); setEngine((e) => e || r.data.default); }); }, []);

  const onDrop = async (e) => {
    e.preventDefault(); setDrag(false);
    const items = Array.from(e.dataTransfer.items || []);
    const collected = [];
    for (const it of items) {
      const entry = it.webkitGetAsEntry?.();
      if (entry) collected.push(...(await readEntry(entry)));
      else if (it.getAsFile()) collected.push({ file: it.getAsFile(), path: it.getAsFile().name });
    }
    setRows(toRows(collected));
  };
  const onPick = (e) => setRows(toRows(Array.from(e.target.files).map((f) => ({ file: f, path: f.webkitRelativePath || f.name }))));
  const update = (id, patch) => setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const setAllProject = (v) => setRows((r) => r.map((x) => ({ ...x, project: v })));

  const queueAll = async () => {
    setRunning(true);
    const mediaIds = [];
    for (const row of rows) {
      if (row.status === "done") { mediaIds.push(row.mediaId); continue; }
      update(row.id, { status: "uploading", pct: 0 });
      const form = new FormData();
      form.append("file", row.file); form.append("project_id", row.project); form.append("episode_id", row.episode);
      try {
        const r = await api.post("/media/upload", form, { onUploadProgress: (ev) => update(row.id, { pct: ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0, status: ev.loaded >= (ev.total || 1) ? "analysing" : "uploading" }) });
        mediaIds.push(r.data.id);
        update(row.id, { status: "done", mediaId: r.data.id, pct: 100 });
      } catch (err) {
        update(row.id, { status: "error", error: errorText(err) });
      }
    }
    if (mediaIds.length) {
      try {
        const r = await api.post("/jobs/batch", { media_ids: mediaIds, engine, preset });
        toast.success(`${r.data.created} separation jobs queued (${preset} · ${engine})`);
        onDone?.();
      } catch (err) { toast.error(errorText(err)); }
    }
    setRunning(false);
  };

  const failed = rows.filter((r) => r.status === "error").length;
  return (
    <div data-testid="batch-intake" className="panel p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold flex items-center gap-2"><Layers size={15} className="text-cyan-400" /> Season batch intake</div>
          <div className="text-xs text-slate-400 mt-0.5">Drop a whole season folder — project = folder name, episode parsed from filename (S01E07 / EP07). Review, then queue every episode with one preset.</div>
        </div>
        <button data-testid="batch-pick-folder-button" onClick={() => inputRef.current?.click()} className="flex items-center gap-2 px-3 h-9 rounded-sm border border-[#3A4A68] hover:bg-[#1E2738] text-sm"><FolderOpen size={14} /> Choose folder</button>
        <input ref={inputRef} data-testid="batch-folder-input" type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={onPick} />
      </div>
      {rows.length === 0 ? (
        <div data-testid="batch-dropzone" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop} onClick={() => inputRef.current?.click()}
          className={`cursor-pointer border border-dashed rounded-md px-6 py-6 text-center text-sm transition-colors ${drag ? "border-cyan-400 bg-cyan-400/5" : "border-[#3A4A68] hover:border-slate-400 hover:bg-[#1A2130]"}`}>
          <span className="text-slate-300">Drop a season folder here</span> <span className="text-slate-500">(or click to choose)</span>
        </div>
      ) : (
        <>
          <div className="flex items-end gap-3 flex-wrap">
            <label className="flex flex-col gap-1"><span className="micro">Project ID (all rows)</span><input data-testid="batch-project-input" value={rows[0]?.project || ""} onChange={(e) => setAllProject(e.target.value)} className={`${inputCls} w-44 py-1.5`} /></label>
            <label className="flex flex-col gap-1"><span className="micro">Preset</span>
              <select data-testid="batch-preset-select" value={preset} onChange={(e) => setPreset(e.target.value)} className={`${inputCls} py-1.5 w-56 font-sans`}>{Object.entries(PRESET_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            <label className="flex flex-col gap-1"><span className="micro">Engine</span>
              <select data-testid="batch-engine-select" value={engine} onChange={(e) => setEngine(e.target.value)} className={`${inputCls} py-1.5 w-56 font-sans`}>{engines.map((e) => <option key={e.key} value={e.key}>{e.name}{e.configured ? "" : " — not configured"}</option>)}</select></label>
            <button data-testid="batch-queue-all-button" disabled={running || !rows.length} onClick={queueAll} className="flex items-center gap-2 px-4 h-9 rounded-sm bg-[#E11D48] hover:bg-[#be123c] text-white text-sm font-semibold disabled:opacity-40">
              {running ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />} {running ? "Uploading & queueing…" : `Upload & queue ${rows.length} episode${rows.length > 1 ? "s" : ""}`}
            </button>
            <button data-testid="batch-clear-button" disabled={running} onClick={() => setRows([])} className="text-xs text-slate-500 hover:text-white ml-auto">clear</button>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="batch-review-table" className="w-full text-sm">
              <thead><tr className="text-left micro border-b border-[#253044]"><th className="px-2 py-2">#</th><th className="px-2 py-2">File</th><th className="px-2 py-2">Size</th><th className="px-2 py-2">Project</th><th className="px-2 py-2">Episode</th><th className="px-2 py-2">Status</th><th className="px-2 py-2"></th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`batch-row-${i}`} className="border-b border-[#1c2433] last:border-0">
                    <td className="px-2 py-1.5 font-mono text-xs text-slate-500">{i + 1}</td>
                    <td className="px-2 py-1.5 font-mono text-xs truncate max-w-[320px]" title={r.path}>{r.path}</td>
                    <td className="px-2 py-1.5 font-mono text-xs text-slate-400">{fmtBytes(r.file.size)}</td>
                    <td className="px-2 py-1.5"><input data-testid={`batch-project-${i}`} value={r.project} disabled={running} onChange={(e) => update(r.id, { project: e.target.value })} className={`${inputCls} w-32`} /></td>
                    <td className="px-2 py-1.5"><input data-testid={`batch-episode-${i}`} value={r.episode} disabled={running} onChange={(e) => update(r.id, { episode: e.target.value })} className={`${inputCls} w-24`} /></td>
                    <td className="px-2 py-1.5 text-xs">
                      {r.status === "pending" && <span className="text-slate-500">ready</span>}
                      {(r.status === "uploading" || r.status === "analysing") && <span className="flex items-center gap-2 text-sky-300"><Loader2 size={11} className="animate-spin" />{r.status} {r.pct}%</span>}
                      {r.status === "done" && <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 size={12} /> ingested</span>}
                      {r.status === "error" && <span className="flex items-center gap-1 text-rose-400" title={r.error}><XCircle size={12} /> {r.error}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right"><button data-testid={`batch-remove-${i}`} disabled={running} onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))} className="p-1 text-slate-500 hover:text-rose-400"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {failed > 0 && !running && <div className="text-xs text-rose-300">{failed} file(s) failed to ingest — fix and click queue again; already-ingested rows are skipped.</div>}
        </>
      )}
    </div>
  );
};
